import { useEffect, useId, useState } from "react";
import { createPortal } from "react-dom";
import { useTranslation } from "react-i18next";
import { TbQrcode, TbSignature, TbMail, TbMessage, TbX, TbLoader2, TbCircleCheck, TbAlertTriangle, TbUser, TbUserOff } from "react-icons/tb";
import Popup from "src/components/Popup/Popup";
import { ConsentModal } from "src/pages/RecipientPage/ConsentModal";
import { apiRequest, ApiError } from "src/services/apiClient";
import { useEscKey } from "src/hooks/useEscKey";
import type { Patient, Recipient } from "src/types";
import { consentNeed, patientName } from "./patientSearch";
import { postForBlob } from "./postForBlob";

type Channel = "digital" | "post";
type Note = { variant: "success" | "error"; text: string };

/** How often the QR dialog re-reads the recipient while the patient signs on their phone —
 *  same rhythm as the patient record's consent QR. */
const QR_POLL_MS = 3000;

/**
 * Step 1, story 11: ask for the consent that sending needs, right where the patient is picked —
 * with the patient at the desk (QR code, paper) or not (link by email or SMS). Post-only patients
 * consent on paper or by QR. Creating the invoice never waits for it (chapter 04 rules).
 * Reuses the patient record's endpoints and its paper-consent dialog.
 */
export function ConsentRequest({
    recipient,
    patient,
    required,
    onChanged,
}: {
    recipient: Recipient;
    /** The invoice's patient — the consent must name them (a newborn isn't in the parent's old one). */
    patient: Patient;
    required: boolean;
    /** Re-read the recipient (consent state) — the wizard's single copy. */
    onChanged: () => Promise<void>;
}) {
    const { t } = useTranslation();
    const c = (key: string, opts?: Record<string, unknown>) => t(`invoiceCreate.consent.${key}`, opts);
    const need = consentNeed(recipient, required, patient.id);
    const name = patientName(recipient);

    const [qr, setQr] = useState<{ channel: Channel; url: string | null } | null>(null);
    const [paper, setPaper] = useState<Channel | null>(null);
    const [emailOpen, setEmailOpen] = useState(false);
    const [smsOpen, setSmsOpen] = useState(false);
    const [busy, setBusy] = useState(false);
    const [note, setNote] = useState<Note | null>(null);

    // The patient signs on their phone: poll until consent shows up, then close the code.
    useEffect(() => {
        if (!qr) return;
        const iv = setInterval(() => void onChanged(), QR_POLL_MS);
        return () => clearInterval(iv);
    }, [qr, onChanged]);
    useEffect(() => {
        if (qr && need.kind === "none") {
            closeQr();
            setNote({ variant: "success", text: c("signed") });
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [need.kind]);

    const closeQr = () => {
        setQr((cur) => {
            if (cur?.url) URL.revokeObjectURL(cur.url);
            return null;
        });
    };

    const errorText = (e: unknown, fallback: string) =>
        e instanceof ApiError && (e.body?.message ?? e.message) === "POST_ONLY" ? c("errors.postOnly") : fallback;

    const openQr = async (channel: Channel) => {
        setNote(null);
        setQr({ channel, url: null });
        try {
            // Digital: the consent form (also completes contact data); post: the postal consent only.
            const url = channel === "digital"
                ? await postForBlob("/patient-portal/questionnaire/create-qr", { recipientId: recipient.id })
                : await postForBlob(`/recipients/${recipient.id}/consent-qr`, { channel: "post" });
            setQr((cur) => (cur ? { ...cur, url } : (URL.revokeObjectURL(url), null)));
        } catch {
            setQr(null);
            setNote({ variant: "error", text: c("errors.qr") });
        }
    };

    const sendEmail = async () => {
        setBusy(true);
        try {
            await apiRequest("/patient-portal/questionnaire/create-link", { method: "POST", body: JSON.stringify({ recipientId: recipient.id }) }, { "Content-Type": "application/json" });
            setNote({ variant: "success", text: c("emailSent", { email: need.kind === "digital" ? need.email : "" }) });
        } catch (e) {
            setNote({ variant: "error", text: errorText(e, c("errors.email")) });
        } finally {
            setBusy(false);
            setEmailOpen(false);
        }
    };

    const sendSms = async (phone: string, lang: "de" | "en") => {
        setBusy(true);
        try {
            await apiRequest("/patient-portal/questionnaire/send-sms", { method: "POST", body: JSON.stringify({ recipientId: recipient.id, phone, lang }) }, { "Content-Type": "application/json" });
            setNote({ variant: "success", text: c("smsSent", { phone }) });
        } catch (e) {
            setNote({ variant: "error", text: errorText(e, c("errors.sms")) });
        } finally {
            setBusy(false);
            setSmsOpen(false);
        }
    };

    if (need.kind === "none" && !note) return null;

    return (
        <div className="ic-consent">
            {need.kind !== "none" && (
                <>
                    <p className="ic-consent__lead">
                        {need.notNamed && !(need.kind === "digital" && need.requested)
                            ? c("leadNotNamed", { patient: patientName(patient) })
                            : need.kind === "post" ? c("leadPost") : need.requested ? c("leadRequested") : c("leadMissing")}
                        {need.kind === "digital" && need.postSigned && <> {c("postAlreadySigned")}</>}
                    </p>

                    <div className="ic-consent__groups">
                        <div className="ic-consent__group" role="group" aria-label={c("present")}>
                            <span className="ic-consent__group-title"><TbUser aria-hidden /> {c("present")}</span>
                            <ActionButton
                                icon={<TbQrcode />}
                                title={c("qr")}
                                hint={need.kind === "post" ? c("qrPostHint") : c("qrHint")}
                                onClick={() => void openQr(need.kind === "post" ? "post" : "digital")}
                            />
                            <ActionButton
                                icon={<TbSignature />}
                                title={c("paper")}
                                hint={c("paperHint")}
                                onClick={() => { setNote(null); setPaper(need.kind === "post" ? "post" : "digital"); }}
                            />
                        </div>

                        {need.kind === "digital" && (
                            <div className="ic-consent__group" role="group" aria-label={c("absent")}>
                                <span className="ic-consent__group-title"><TbUserOff aria-hidden /> {c("absent")}</span>
                                <ActionButton
                                    icon={<TbMail />}
                                    title={need.requested ? c("emailAgain") : c("email")}
                                    hint={need.email || c("noEmail")}
                                    disabled={!need.email}
                                    onClick={() => { setNote(null); setEmailOpen(true); }}
                                />
                                <ActionButton
                                    icon={<TbMessage />}
                                    title={need.requested ? c("smsAgain") : c("sms")}
                                    hint={need.phones.length ? `${need.phones[0]}${need.phones.length > 1 ? ` +${need.phones.length - 1}` : ""} · ${c("smsCost")}` : c("noPhone")}
                                    disabled={need.phones.length === 0}
                                    onClick={() => { setNote(null); setSmsOpen(true); }}
                                />
                            </div>
                        )}
                    </div>
                </>
            )}

            {note && (
                <div className={`ic-note ic-note--${note.variant}`} role={note.variant === "error" ? "alert" : "status"}>
                    {note.variant === "success" ? <TbCircleCheck aria-hidden /> : <TbAlertTriangle aria-hidden />}
                    <span>{note.text}</span>
                </div>
            )}

            {qr && (
                <QrDialog
                    url={qr.url}
                    name={name}
                    title={qr.channel === "post" ? c("qrPostTitle") : c("qrTitle")}
                    hint={qr.channel === "post" ? c("qrPostHint") : c("qrDialogHint")}
                    onClose={closeQr}
                />
            )}

            {paper && (
                <ConsentModal
                    recipientId={recipient.id}
                    recipientName={name}
                    patientNames={(recipient.patients ?? []).map(patientName)}
                    channel={paper}
                    onClose={() => setPaper(null)}
                    onSaved={() => {
                        setNote({ variant: "success", text: c("paperSaved") });
                        void onChanged();
                    }}
                />
            )}

            {emailOpen && need.kind === "digital" && (
                <Popup
                    title={need.requested ? c("emailAgain") : c("email")}
                    message={<p>{c("emailConfirm", { name })} <strong>{need.email}</strong></p>}
                    actions={[
                        { label: t("popup.cancel"), variant: "default", onClick: () => setEmailOpen(false) },
                        { label: busy ? c("sending") : c("send"), variant: "primary", disabled: busy, onClick: () => void sendEmail() },
                    ]}
                    onClose={() => setEmailOpen(false)}
                />
            )}

            {smsOpen && need.kind === "digital" && (
                <SmsDialog
                    phones={need.phones}
                    title={need.requested ? c("smsAgain") : c("sms")}
                    busy={busy}
                    onSend={(phone, lang) => void sendSms(phone, lang)}
                    onClose={() => setSmsOpen(false)}
                />
            )}
        </div>
    );
}

function ActionButton({ icon, title, hint, disabled, onClick }: { icon: React.ReactNode; title: string; hint: string; disabled?: boolean; onClick: () => void }) {
    return (
        <button type="button" className="ic-consent__action" disabled={disabled} onClick={onClick}>
            <span className="ic-consent__action-icon" aria-hidden>{icon}</span>
            <span className="ic-consent__action-text">
                <strong>{title}</strong>
                <small>{hint}</small>
            </span>
        </button>
    );
}

function QrDialog({ url, name, title, hint, onClose }: { url: string | null; name: string; title: string; hint: string; onClose: () => void }) {
    const { t } = useTranslation();
    const titleId = useId();
    useEscKey(onClose, true);
    return createPortal(
        <div className="ic-pdf-overlay" onClick={onClose}>
            <div className="ic-qr-modal" role="dialog" aria-modal="true" aria-labelledby={titleId} onClick={(e) => e.stopPropagation()}>
                <div className="ic-pdf-modal__header">
                    <span id={titleId}>{title}</span>
                    <button type="button" className="ic-pdf-modal__close" onClick={onClose} aria-label={t("popup.close")}>
                        <TbX aria-hidden />
                    </button>
                </div>
                <div className="ic-qr-modal__body">
                    <p className="ic-qr-modal__hint">{hint}</p>
                    {url ? (
                        <img className="ic-qr-modal__img" src={url} alt={t("invoiceCreate.consent.qrAlt", { name })} />
                    ) : (
                        <span className="ic-qr-modal__placeholder" role="status">
                            <TbLoader2 aria-hidden /> {t("invoiceCreate.consent.qrLoading")}
                        </span>
                    )}
                    <p className="ic-qr-modal__name">{name}</p>
                    {url && (
                        <p className="ic-qr-modal__waiting" role="status">
                            <TbLoader2 aria-hidden /> {t("invoiceCreate.consent.waiting")}
                        </p>
                    )}
                </div>
            </div>
        </div>,
        document.body,
    );
}

function SmsDialog({ phones, title, busy, onSend, onClose }: {
    phones: string[];
    title: string;
    busy: boolean;
    onSend: (phone: string, lang: "de" | "en") => void;
    onClose: () => void;
}) {
    const { t } = useTranslation();
    const c = (key: string) => t(`invoiceCreate.consent.${key}`);
    const phoneId = useId();
    const langId = useId();
    const [phone, setPhone] = useState(phones[0] ?? "");
    const [lang, setLang] = useState<"de" | "en">("de");
    return (
        <Popup
            title={title}
            message={
                <div className="ic-sms">
                    <p className="ic-sms__cost">{c("smsCostNote")}</p>
                    <label className="ic-sms__field" htmlFor={phoneId}>
                        <span>{c("smsTo")}</span>
                        {phones.length > 1 ? (
                            <select id={phoneId} className="ic-sms__select" value={phone} onChange={(e) => setPhone(e.target.value)}>
                                {phones.map((p) => <option key={p} value={p}>{p}</option>)}
                            </select>
                        ) : (
                            <strong id={phoneId}>{phone}</strong>
                        )}
                    </label>
                    <label className="ic-sms__field" htmlFor={langId}>
                        <span>{c("smsLanguage")}</span>
                        <select id={langId} className="ic-sms__select" value={lang} onChange={(e) => setLang(e.target.value as "de" | "en")}>
                            <option value="de">Deutsch</option>
                            <option value="en">English</option>
                        </select>
                    </label>
                </div>
            }
            actions={[
                { label: t("popup.cancel"), variant: "default", onClick: onClose },
                { label: busy ? c("sending") : c("send"), variant: "primary", disabled: busy || !phone, onClick: () => onSend(phone, lang) },
            ]}
            onClose={onClose}
        />
    );
}
